import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cancelCleaningJob, deleteTestClean } from "@/lib/testClean";
import { Calendar, FlaskConical, Loader2, RefreshCw } from "lucide-react";
import { TestCleanLauncher } from "@/components/owner/TestCleanLauncher";
import { cn } from "@/lib/utils";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import {
  type ReservationDateRow,
  type TurnoverChipState,
  chipClassName,
  formatLastSyncLabel,
  resolveTurnoverWindow,
} from "@/lib/turnoverWindow";
import { OwnerGroupSentences } from "./OwnerGroupSentences";
import {
  collectSimplePhotoUrls,
  readSimpleFlowState,
  simpleLivePhaseLabel,
} from "@/lib/simpleTurnover";
import { format } from "date-fns";

export type SimpleJobStatusLabel = TurnoverChipState;

function CleanActionButton({
  taskId,
  className,
  label,
  busyLabel,
  confirmText,
  doneText,
  action,
}: {
  taskId: string;
  className: string;
  label: string;
  busyLabel: string;
  confirmText: string;
  doneText: string;
  action: (taskId: string) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      className={className}
      onClick={async () => {
        if (!window.confirm(confirmText)) return;
        setBusy(true);
        try {
          await action(taskId);
          await queryClient.invalidateQueries();
          toast.success(doneText);
        } catch (e) {
          toast.error((e as { message?: string })?.message || "Couldn't do that");
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? busyLabel : label}
    </button>
  );
}

const DeleteTestButton = ({ taskId, className }: { taskId: string; className: string }) => (
  <CleanActionButton
    taskId={taskId}
    className={className}
    label="Delete test"
    busyLabel="Deleting…"
    confirmText="Delete this test clean and everything from it?"
    doneText="Test clean deleted"
    action={deleteTestClean}
  />
);

/** A real clean that is live: removes the job and its photos and reports; the stay stays, unassigned. */
const CancelCleanButton = ({ taskId, className, who }: { taskId: string; className: string; who: string }) => (
  <CleanActionButton
    taskId={taskId}
    className={className}
    label="Cancel clean"
    busyLabel="Cancelling…"
    confirmText={`Cancel this clean? ${who}'s progress, photos and reports for it are deleted. The turnover stays on the Schedule, unassigned. This can't be undone.`}
    doneText="Clean cancelled"
    action={cancelCleaningJob}
  />
);

/** @deprecated time windows removed — kept for any stray imports */
export function formatJobWindow(_task: CleaningTaskWithRelations): string {
  return "";
}

export function jobStatusLabel(task: CleaningTaskWithRelations, assigned: boolean): SimpleJobStatusLabel {
  if (task.status === "completed") return "Done";
  if (task.status === "in_progress") return "In progress";
  if (!assigned) return "Upcoming";
  return "Open";
}

function initialOf(name: string): string {
  const t = name.trim();
  return t ? t[0]!.toUpperCase() : "?";
}

export type ScheduleJobsView = "list" | "month";

export interface SimpleScheduleJobsProps {
  tasks: CleaningTaskWithRelations[];
  reservations: ReservationDateRow[];
  propertyId: string;
  syncing: boolean;
  syncFailed?: boolean;
  lastSyncAt: number | null;
  canAddTasks: boolean;
  hasIcalUrls: boolean;
  isAssigned: (task: CleaningTaskWithRelations) => boolean;
  getAssigneeLabel: (task: CleaningTaskWithRelations) => string;
  onSync: () => void;
  onConnectIcal?: () => void;
  onOpenJob: (task: CleaningTaskWithRelations) => void;
  /** Slice 37 — open live Check unit desk */
  onOpenLiveJob?: (task: CleaningTaskWithRelations) => void;
  onAssign: (task: CleaningTaskWithRelations) => void;
  /** Temporary dogfood: open Create test clean modal */
  onCreateTestClean?: () => void;
  loading?: boolean;
  /** Slice 43 — List ↔ Month switcher */
  scheduleView?: ScheduleJobsView;
  onScheduleViewChange?: (view: ScheduleJobsView) => void;
  /** Month view body (grid + optional side panel) */
  monthContent?: ReactNode;
}

type RowModel = {
  task: CleaningTaskWithRelations;
  isTest: boolean;
  chip: TurnoverChipState;
  window: string;
  outSentence: string | null;
  inSentence: string | null;
  statusNote: string | null;
  outGuestName: string | null;
  inGuestName: string | null;
  outGuestPhotoUrl: string | null;
  inGuestPhotoUrl: string | null;
  assigned: boolean;
  cleaner: string;
};

function sectionFor(chip: TurnoverChipState, assigned: boolean): "attention" | "open" | "upcoming" | "done" {
  if (chip === "Done") return "done";
  if (chip === "Upcoming") return "upcoming";
  if (
    chip === "Overdue" ||
    chip === "Same day" ||
    chip === "Waiting on guest" ||
    (chip === "Open" && !assigned)
  ) {
    return "attention";
  }
  return "open";
}

export function SimpleScheduleJobs({
  tasks,
  reservations,
  propertyId,
  syncing,
  syncFailed,
  lastSyncAt,
  canAddTasks,
  hasIcalUrls,
  isAssigned,
  getAssigneeLabel,
  onSync,
  onConnectIcal,
  onOpenJob,
  onOpenLiveJob,
  onAssign,
  onCreateTestClean,
  loading,
  scheduleView = "list",
  onScheduleViewChange,
  monthContent,
}: SimpleScheduleJobsProps) {
  const handleSyncClick = () => {
    if (hasIcalUrls) onSync();
    else onConnectIcal?.();
  };

  const resById = new Map(reservations.map((r) => [r.id, r]));

  const rows: RowModel[] = tasks.map((task) => {
    const assigned = isAssigned(task);
    const linked = task.reservation_id ? resById.get(task.reservation_id) : undefined;
    const isTest =
      !!(task as { is_test?: boolean }).is_test ||
      !!linked?.is_test ||
      linked?.summary === "Test clean";
    const checkout =
      (task as { reservation?: { end_date?: string } | null }).reservation?.end_date ||
      linked?.end_date ||
      task.scheduled_date;
    const resolved = resolveTurnoverWindow({
      checkoutDate: checkout,
      reservations,
      departingReservationId: task.reservation_id,
      taskStatus: task.status,
    });
    return {
      task,
      chip: resolved.chip,
      window: isTest ? "Test clean" : resolved.label,
      outSentence: isTest ? null : resolved.outSentence,
      inSentence: isTest ? null : resolved.inSentence,
      statusNote: isTest ? null : resolved.statusNote,
      outGuestName: isTest ? null : resolved.outGuestName,
      inGuestName: isTest ? null : resolved.inGuestName,
      outGuestPhotoUrl: isTest ? null : resolved.outGuestPhotoUrl,
      inGuestPhotoUrl: isTest ? null : resolved.inGuestPhotoUrl,
      assigned,
      cleaner: assigned ? getAssigneeLabel(task) : "",
      isTest,
    };
  });

  const attention = rows.filter((r) => sectionFor(r.chip, r.assigned) === "attention");
  const open = rows.filter((r) => sectionFor(r.chip, r.assigned) === "open");
  const upcoming = rows.filter((r) => sectionFor(r.chip, r.assigned) === "upcoming");
  const done = rows.filter((r) => sectionFor(r.chip, r.assigned) === "done").slice(0, 5);

  const meta = formatLastSyncLabel(lastSyncAt, syncing, syncFailed);

  const renderDesktopRow = (r: RowModel, i: number, total: number) => {
    const unassigned = !r.assigned;
    const isLive = r.task.status === "in_progress";
    const phase = isLive ? simpleLivePhaseLabel(r.task.entry_walkthrough_state) : null;
    const photoCount = isLive
      ? collectSimplePhotoUrls(readSimpleFlowState(r.task.entry_walkthrough_state), "before").length
      : 0;
    const startedLabel =
      isLive && r.task.started_at
        ? format(new Date(r.task.started_at), "HH:mm")
        : null;
    return (
      <div
        key={r.task.id}
        className={cn(
          "flex w-full items-start gap-4 px-[18px] py-4 text-left",
          i < total - 1 && "border-b-[1.5px] border-[#e6edf1]",
          unassigned ? "bg-[#fffdf8]" : "bg-white",
        )}
      >
        {isLive ? (
          <span className="mt-0.5 inline-flex w-[118px] shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#dc2626] px-3 py-1.5 text-[12px] font-bold tracking-[0.3px] text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            Live
          </span>
        ) : (
          <span
            className={cn(
              "mt-0.5 inline-flex w-[118px] shrink-0 items-center whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-bold tracking-[0.3px]",
              chipClassName(r.chip),
            )}
          >
            {r.chip}
          </span>
        )}
        <button
          type="button"
          onClick={() => (isLive && onOpenLiveJob ? onOpenLiveJob(r.task) : onOpenJob(r.task))}
          className="min-w-0 flex-1 text-left transition-colors hover:opacity-90"
        >
          <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">{r.window}</div>
          {isLive ? (
            <div className="mt-1 text-[13.5px] font-medium text-[#64748b]">
              {r.cleaner || "Cleaner"}
              {phase ? ` · on ${phase}` : ""}
              {startedLabel ? ` · started ${startedLabel}` : ""}
              {` · ${photoCount} photo${photoCount === 1 ? "" : "s"} in`}
            </div>
          ) : (
            <>
              <OwnerGroupSentences
                outSentence={r.outSentence}
                inSentence={r.inSentence}
                outGuestPhotoUrl={r.outGuestPhotoUrl}
                inGuestPhotoUrl={r.inGuestPhotoUrl}
                outGuestName={r.outGuestName}
                inGuestName={r.inGuestName}
                showFaces
                className="mt-1"
              />
              {r.statusNote ? (
                <div className="mt-1 text-[12.5px] font-bold text-[#9a6700]">{r.statusNote}</div>
              ) : null}
            </>
          )}
        </button>
        <div className="flex shrink-0 items-center gap-3">
          {isLive && r.isTest ? <DeleteTestButton taskId={r.task.id} className="h-10 rounded-[12px] border border-[#f0c4c4] bg-white px-4 text-[14px] font-bold text-[#c0392b] hover:bg-[#fdf1f1]" /> : null}
          {isLive && !r.isTest ? (
            <CancelCleanButton
              taskId={r.task.id}
              who={r.cleaner || "The cleaner"}
              className="h-10 rounded-[12px] border border-[#f0c4c4] bg-white px-4 text-[14px] font-bold text-[#c0392b] hover:bg-[#fdf1f1]"
            />
          ) : null}
          {isLive ? (
            <button
              type="button"
              onClick={() => (onOpenLiveJob ? onOpenLiveJob(r.task) : onOpenJob(r.task))}
              className="h-10 rounded-[12px] bg-[#24a89a] px-4 text-[14px] font-bold text-white hover:bg-[#1f958a]"
            >
              Open live job
            </button>
          ) : r.assigned ? (
            <div className="flex items-center gap-2">
              <div className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#e2eeed] text-[14px] font-bold text-[#15776d]">
                {initialOf(r.cleaner)}
              </div>
              <span className="text-[15px] font-bold text-[#0f1720]">{r.cleaner}</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onAssign(r.task)}
              className="h-10 rounded-[12px] bg-[#24a89a] px-4 text-[14px] font-bold text-white hover:bg-[#1f958a]"
            >
              Assign
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPhoneCard = (r: RowModel) => {
    const unassigned = !r.assigned;
    const isLive = r.task.status === "in_progress";
    const phase = isLive ? simpleLivePhaseLabel(r.task.entry_walkthrough_state) : null;
    const photoCount = isLive
      ? collectSimplePhotoUrls(readSimpleFlowState(r.task.entry_walkthrough_state), "before").length
      : 0;
    const startedLabel =
      isLive && r.task.started_at
        ? format(new Date(r.task.started_at), "HH:mm")
        : null;
    return (
      <div
        key={r.task.id}
        className={cn(
          "flex w-full flex-col gap-[7px] rounded-2xl border-[1.5px] border-[#e6edf1] px-4 py-3.5 text-left",
          unassigned ? "bg-[#fffdf8]" : "bg-white",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          {isLive ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#dc2626] px-3 py-1.5 text-[12px] font-bold tracking-[0.3px] text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
              Live
            </span>
          ) : (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-bold tracking-[0.3px]",
                chipClassName(r.chip),
              )}
            >
              {r.chip}
            </span>
          )}
          {isLive && r.isTest ? <DeleteTestButton taskId={r.task.id} className="h-9 rounded-[11px] border border-[#f0c4c4] bg-white px-3 text-[13px] font-bold text-[#c0392b]" /> : null}
          {isLive && !r.isTest ? (
            <CancelCleanButton
              taskId={r.task.id}
              who={r.cleaner || "The cleaner"}
              className="h-9 rounded-[11px] border border-[#f0c4c4] bg-white px-3 text-[13px] font-bold text-[#c0392b]"
            />
          ) : null}
          {isLive ? (
            <button
              type="button"
              onClick={() => (onOpenLiveJob ? onOpenLiveJob(r.task) : onOpenJob(r.task))}
              className="h-9 rounded-[11px] bg-[#24a89a] px-3.5 text-[13px] font-bold text-white"
            >
              Open live job
            </button>
          ) : unassigned ? (
            <button
              type="button"
              onClick={() => onAssign(r.task)}
              className="h-9 rounded-[11px] bg-[#24a89a] px-3.5 text-[13px] font-bold text-white"
            >
              Assign
            </button>
          ) : (
            <span className="text-[14px] font-bold text-[#0f1720]">{r.cleaner}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => (isLive && onOpenLiveJob ? onOpenLiveJob(r.task) : onOpenJob(r.task))}
          className="text-left"
        >
          <div className="text-[18px] font-bold tracking-[-0.3px] text-[#0f1720]">{r.window}</div>
          {isLive ? (
            <div className="mt-1 text-[13px] font-medium text-[#64748b]">
              {r.cleaner || "Cleaner"}
              {phase ? ` · on ${phase}` : ""}
              {startedLabel ? ` · started ${startedLabel}` : ""}
              {` · ${photoCount} photos in`}
            </div>
          ) : (
            <>
              <OwnerGroupSentences
                outSentence={r.outSentence}
                inSentence={r.inSentence}
                outGuestPhotoUrl={r.outGuestPhotoUrl}
                inGuestPhotoUrl={r.inGuestPhotoUrl}
                outGuestName={r.outGuestName}
                inGuestName={r.inGuestName}
                showFaces
              />
              {r.statusNote ? (
                <div className="text-[12.5px] font-bold text-[#9a6700]">{r.statusNote}</div>
              ) : null}
            </>
          )}
        </button>
      </div>
    );
  };

  const Section = ({
    label,
    items,
    phone,
  }: {
    label: string;
    items: RowModel[];
    phone?: boolean;
  }) => {
    if (!items.length) return null;
    return (
      <div className="flex flex-col gap-2.5">
        <div
          className={cn(
            "text-[19px] font-medium tracking-[-0.2px]",
            label === "Needs attention" ? "text-[#92560a]" : "text-[#0f1720]",
          )}
        >
          {label}
        </div>
        {phone ? (
          <div className="flex flex-col gap-3">{items.map(renderPhoneCard)}</div>
        ) : (
          <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
            {items.map((r, i) => renderDesktopRow(r, i, items.length))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5 min-w-0" data-tour="tour-schedule-simple">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap items-center gap-2">
          {onScheduleViewChange ? (
            <div
              className="order-first mr-auto flex rounded-[14px] bg-white p-1"
              role="tablist"
              aria-label="Schedule view"
              data-tour="tour-schedule-view-switcher"
            >
              <button
                type="button"
                role="tab"
                aria-selected={scheduleView === "list"}
                onClick={() => onScheduleViewChange("list")}
                className={cn(
                  "rounded-[10px] px-[22px] py-[9px] text-[14px] font-bold transition-colors max-sm:px-[22px]",
                  scheduleView === "list" ? "bg-[#15776d] text-white" : "text-[#64748b]",
                )}
              >
                List
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={scheduleView === "month"}
                onClick={() => onScheduleViewChange("month")}
                className={cn(
                  "rounded-[10px] px-[22px] py-[9px] text-[14px] font-bold transition-colors max-sm:px-[22px]",
                  scheduleView === "month" ? "bg-[#15776d] text-white" : "text-[#64748b]",
                )}
              >
                Month
              </button>
            </div>
          ) : null}
          {canAddTasks && onCreateTestClean && scheduleView === "list" ? (
            <span data-tour="tour-schedule-test-clean">
              <TestCleanLauncher
                propertyId={propertyId}
                variant="property"
                className="h-11 rounded-[14px] border-0 bg-[#fdf0d5] px-4 text-[#92560a] hover:bg-[#fbe7bd]"
              />
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSyncClick}
            disabled={syncing}
            className={cn(
              "inline-flex h-11 items-center gap-2 rounded-[14px] bg-white px-4 text-[14px] font-bold transition-colors disabled:opacity-60 max-sm:w-11 max-sm:justify-center max-sm:px-0",
              syncing ? "text-[#94a3b8]" : "text-[#334155] hover:text-[#15776d]",
            )}
            data-tour="tour-schedule-sync"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-[17px] w-[17px]" />}
            <span className="max-sm:hidden">{syncing ? "Syncing…" : "Sync"}</span>
          </button>
        </div>
      </div>
      {meta ? (
        <p className={cn("-mt-2 text-[13px] font-medium", syncFailed ? "text-[#b4453a]" : "text-[#94a3b8]")}>{meta}</p>
      ) : null}

      {scheduleView === "month" ? (
        loading ? (
          <div className="flex justify-center py-16 text-[#64748b]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          monthContent
        )
      ) : loading ? (
        <div className="flex justify-center py-16 text-[#64748b]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !hasIcalUrls && rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-5 px-6 py-20 text-center" data-tour="tour-schedule-empty">
          <div className="flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#e7edf1]">
            <Calendar className="h-11 w-11 text-[#64748b]" strokeWidth={1.9} />
          </div>
          <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">No turnovers</div>
          <p className="text-[15px] font-medium text-[#64748b]">
            Link calendar in{" "}
            <button type="button" onClick={() => onConnectIcal?.()} className="font-bold text-[#24a89a] hover:underline">
              Settings
            </button>
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-6 px-6 py-20 text-center" data-tour="tour-schedule-empty">
          <div className="flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#e7edf1]">
            <Calendar className="h-11 w-11 text-[#64748b]" strokeWidth={1.9} />
          </div>
          <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">No turnovers</div>
          <button
            type="button"
            onClick={handleSyncClick}
            disabled={syncing}
            className="inline-flex h-[58px] items-center gap-2.5 rounded-[15px] bg-[#24a89a] px-[30px] text-lg font-bold text-white hover:bg-[#1f958a] disabled:opacity-60"
          >
            {syncing ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-[19px] w-[19px]" />}
            Sync
          </button>
        </div>
      ) : (
        <>
          <div className="hidden flex-col gap-7 sm:flex">
            <Section label="Needs attention" items={attention} />
            <Section label="Open" items={open} />
            <Section label="Upcoming" items={upcoming} />
            <Section label="Done" items={done} />
          </div>
          <div className="flex flex-col gap-6 sm:hidden">
            <Section label="Needs attention" items={attention} phone />
            <Section label="Open" items={open} phone />
            <Section label="Upcoming" items={upcoming} phone />
            <Section label="Done" items={done.slice(0, 3)} phone />
          </div>
        </>
      )}
      {/* canAddTasks kept in props for API compat; + Job buried per Slice 12 */}
      {canAddTasks ? null : null}
    </div>
  );
}

